import type { FrameName, FrameVariantName } from "odyssey-shared";
import { TICK_SIZE } from "./metrics.ts";

/**
 * The kit's state tables (brief rule 10: default, hover, pressed, focus, disabled, selected and error where
 * input can fail): which atlas frame each control draws in each state, and how the arrow keys move
 * through a group of choices. A state that changes a frame's pixels is a frame of its own in the atlas
 * (`FRAME_VARIANTS`), drawn through its generated `border-image` class; keyboard focus on a button, a link
 * or a checkbox is an outline the stylesheet draws, so it changes no frame.
 */

/**
 * A frame the kit draws: one the export declares or one of the kit's variants.
 */
export type KitFrame = FrameName | FrameVariantName;

/**
 * A button's variant: primary for the one action a surface leads with, secondary for the rest, danger for
 * an action that destroys or cannot be undone.
 */
export type ButtonVariant = "primary" | "secondary" | "danger";

/**
 * What the pointer and the keyboard are doing to a control.
 */
export interface Interaction {
  /** The pointer is over it. */
  readonly hover: boolean;
  /** It is held down by the pointer, or by Space or Enter while focused. */
  readonly pressed: boolean;
  /** It has keyboard focus: `:focus-visible` for a button, any focus for a text field. */
  readonly focus: boolean;
}

/**
 * No interaction.
 */
export const AT_REST: Interaction = { hover: false, pressed: false, focus: false };

/**
 * The frame a button draws. Disabled wins over everything, then pressed, then selected, then hover; a
 * primary or danger button is never selected, and every variant shares the disabled frame, except that a
 * disabled secondary button that is selected keeps a frame of its own, so a choice that cannot be changed where
 * it stands still shows which option is chosen.
 *
 * @param variant The variant.
 * @param state The interaction and the button's own states.
 * @returns The frame.
 */
export function buttonFrame(variant: ButtonVariant, state: Interaction & { readonly disabled: boolean; readonly selected: boolean }): KitFrame {
  if (state.disabled) {
    return variant === "secondary" && state.selected ? "button_disabled_selected" : "button_disabled";
  }
  if (variant === "primary") {
    return state.pressed ? "button-primary_pressed" : state.hover ? "button-primary_hover" : "button-primary";
  }
  if (variant === "danger") {
    return state.pressed ? "button-danger_pressed" : state.hover ? "button-danger_hover" : "button-danger";
  }
  return state.pressed ? "button_pressed" : state.selected ? "button_selected" : state.hover ? "button_hover" : "button";
}

/**
 * The frame a text field's box draws. Disabled wins; a focused field shows the aether focus, with the
 * danger ring inside it when its input has failed; an error shows whether or not the pointer is over it.
 *
 * @param state The interaction and the field's own states.
 * @returns The frame.
 */
export function fieldFrame(state: Interaction & { readonly disabled: boolean; readonly error: boolean }): KitFrame {
  if (state.disabled) {
    return "field_disabled";
  }
  if (state.focus) {
    return state.error ? "field_focus_error" : "field_focus";
  }
  if (state.error) {
    return "field_error";
  }
  return state.hover ? "field_hover" : "field";
}

/**
 * The frame a checkbox's box draws, the field frame in its states; keyboard focus is an outline around it
 * and a checked box shows the tick inside it.
 *
 * @param state The interaction and whether the checkbox is disabled.
 * @returns The frame.
 */
export function checkFrame(state: Interaction & { readonly disabled: boolean }): KitFrame {
  if (state.disabled) {
    return "field_disabled";
  }
  return state.pressed ? "field_pressed" : state.hover ? "field_hover" : "field";
}

/**
 * The generated class that draws a frame by `border-image` (`ui-atlas.css`).
 *
 * @param frame The frame.
 * @returns `ot-ui-frame-<frame>`.
 */
export function frameClass(frame: KitFrame): string {
  return `ot-ui-frame-${frame}`;
}

/**
 * The generated class that draws the checkbox tick by `background-position`.
 *
 * @param disabled Whether the checkbox is disabled, which draws the tick in the disabled text colour.
 * @returns `ot-ui-check-tick_6` or `ot-ui-check-tick-disabled_6`.
 */
export function tickClass(disabled: boolean): string {
  return `ot-ui-check-tick${disabled ? "-disabled" : ""}_${TICK_SIZE}`;
}

/**
 * The next choice the arrow keys reach in a group, skipping disabled ones and wrapping at either end.
 *
 * @param disabled Whether each choice is disabled, in order.
 * @param from The index to move from; -1 before the first.
 * @param step 1 for the next choice (right or down), -1 for the previous (left or up).
 * @returns The index reached, or -1 when every choice is disabled.
 * @example
 * nextEnabled([false, true, false], 0, 1);
 * // 2
 */
export function nextEnabled(disabled: readonly boolean[], from: number, step: 1 | -1): number {
  const count = disabled.length;
  for (let moved = 1; moved <= count; moved += 1) {
    const index = (((from + step * moved) % count) + count) % count;
    if (disabled[index] === false) {
      return index;
    }
  }
  return -1;
}

/**
 * Which key moves through a group of choices, and how: arrows move by one and wrap, Home and End go to the
 * first and last enabled choice. A group laid out across (a segmented choice) answers all four arrows; a
 * vertical one (a menu, a list) only up and down, and leaves left and right alone.
 *
 * @param key The `KeyboardEvent.key` value.
 * @param disabled Whether each choice is disabled, in order.
 * @param from The index focus is on, or -1.
 * @param axis `both` for a group that answers every arrow, `vertical` for one that answers up and down.
 * @returns The index to select and focus, -1 when every choice is disabled, or null when the key does not
 *   move through the group.
 * @example
 * choiceForKey("ArrowDown", [false, true, false], 0, "vertical");
 * // 2
 */
export function choiceForKey(key: string, disabled: readonly boolean[], from: number, axis: "both" | "vertical" = "both"): number | null {
  const across = axis === "both";
  switch (key) {
    case "ArrowRight":
      return across ? nextEnabled(disabled, from, 1) : null;
    case "ArrowDown":
      return nextEnabled(disabled, from, 1);
    case "ArrowLeft":
      return across ? nextEnabled(disabled, from === -1 ? 0 : from, -1) : null;
    case "ArrowUp":
      return nextEnabled(disabled, from === -1 ? 0 : from, -1);
    case "Home":
      return nextEnabled(disabled, -1, 1);
    case "End":
      return nextEnabled(disabled, 0, -1);
    default:
      return null;
  }
}

/**
 * Whether a key is one the browser activates a focused control with: Space or Enter on a button, Space on
 * a checkbox or a radio input, Enter on a link. The kit stops such a key at the control, so neither the
 * input router nor the game's keyboard sees it, while the browser's own activation still runs.
 *
 * @param control The control's tag name, in either case, and its `type` attribute (empty when it has none).
 * @param key The `KeyboardEvent.key` value.
 * @returns True when the key activates the control.
 * @example
 * activatesOn({ tag: "INPUT", type: "checkbox" }, "Enter");
 * // false
 */
export function activatesOn(control: { readonly tag: string; readonly type: string }, key: string): boolean {
  switch (control.tag.toUpperCase()) {
    case "BUTTON":
      return key === " " || key === "Enter";
    case "INPUT":
      return key === " " && (control.type === "checkbox" || control.type === "radio");
    case "A":
      return key === "Enter";
    default:
      return false;
  }
}
